import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface PresetContentWarning {
  id: string;
  warning_name: string;
  category: string;
  description: string | null;
  severity: 'mild' | 'moderate' | 'severe';
  sort_order: number;
}

export interface WarningsByCategory {
  [category: string]: PresetContentWarning[];
}

const CATEGORY_ORDER = [
  'violence',
  'mental_health',
  'relationships',
  'trauma',
  'death',
  'substance',
  'identity',
  'other'
];

const CATEGORY_LABELS: Record<string, string> = {
  violence: 'Violence',
  mental_health: 'Mental Health',
  relationships: 'Relationships',
  trauma: 'Trauma',
  death: 'Death',
  substance: 'Substance Use',
  identity: 'Identity',
  other: 'Other'
};

export function usePresetContentWarnings() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['preset-content-warnings'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('preset_content_warnings')
        .select('*')
        .order('category')
        .order('sort_order');

      if (error) throw error;
      return data as PresetContentWarning[];
    },
    staleTime: 1000 * 60 * 60, // 1 hour - these rarely change
  });

  const warningsByCategory: WarningsByCategory = {};
  
  if (data) {
    for (const warning of data) {
      if (!warningsByCategory[warning.category]) {
        warningsByCategory[warning.category] = [];
      }
      warningsByCategory[warning.category].push(warning);
    }
  }

  const sortedCategories = CATEGORY_ORDER.filter(cat => warningsByCategory[cat]);

  return {
    warnings: data || [],
    warningsByCategory,
    sortedCategories,
    categoryLabels: CATEGORY_LABELS,
    isLoading,
    error,
  };
}
